// src/features/feeds/FeedUtils.ts

import { MediaStateType } from '@lib2/model/media/MediaTypes';
import { ITunesExplicit } from '@lib2/model/itunes/ItunesCategories';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import type { FeedData, FeedFormData, FeedCreateRequest, FeedSourceData, FeedGridRow } from '@/lib/types/Feed';

export class FeedUtils {
  /** API -> Form: null to empty string for form inputs. */
  static transformFeedToFormData(feed: FeedData): FeedFormData {
    return {
      title: feed.title,
      descShort: feed.descShort ?? '',
      mediaStateType: feed.mediaStateType,
      language: feed.language,
      itunesExplicit: feed.itunesExplicit,
    };
  }

  /** Form -> API: empty string to null, include current sources. */
  static transformFormToUpdateRequest(data: FeedFormData, sources: FeedSourceData[]): FeedCreateRequest {
    return {
      title: data.title,
      descShort: data.descShort || null,
      mediaStateType: data.mediaStateType as MediaStateType,
      language: data.language,
      itunesCategory: null,    // Not exposed in UI
      itunesExplicit: data.itunesExplicit as ITunesExplicit,
      sources: sources.map((s, i) => ({
        listId: s.listId,
        position: i,
        title: s.title,
      })),
    };
  }

  /** Create request from minimal form (create dialog). */
  static createNewFeedRequest(title: string): FeedCreateRequest {
    return {
      title,
      descShort: null,
      mediaStateType: MediaStateType.Active,
      language: 'en',
      itunesCategory: null,
      itunesExplicit: ITunesExplicit.No,
      sources: [],
    };
  }

  /** Base-converted display ID for URLs and display. */
  static getDisplayId(feedId: number): string {
    return bcLong.getStr(feedId);
  }

  /** Decode display ID from URL param back to numeric. */
  static getNumericId(displayId: string): number {
    return bcLong.getNum(displayId);
  }

  /** Build FeedGridRow from FeedData -- pre-computed display values. */
  static buildRow(feed: FeedData): FeedGridRow {
    const displayId = bcLong.getStr(feed.feedId);
    const isActive = feed.mediaStateType === MediaStateType.Active;

    return {
      feedId: feed.feedId,
      displayId,
      title: feed.title,
      status: feed.mediaStateType,
      sourceCount: feed.sources.length,
      createdFormatted: feed.created ? formatSmartDate(feed.created) : null,
      createdTooltip: feed.created ? formatTimestamp(feed.created) : null,
      updatedFormatted: formatSmartDate(feed.updated),
      updatedTooltip: formatTimestamp(feed.updated),
      isActive,
      href: `/feeds/${displayId}`,
    };
  }
}
